import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Logiciel de trésorerie association : gérer subventions et cash facilement | Trezoone',
    description: 'Le logiciel de trésorerie pensé pour les associations : suivi des subventions, alertes sur les retards de versement, plan annuel, rapports pour le CA.',
    canonical: '/logiciel-tresorerie-association',
    keywords: 'logiciel trésorerie association, gestion trésorerie association, outil trésorerie association, logiciel association subventions',
  },
  hero: {
    tag: 'Logiciel trésorerie association',
    title: 'La subvention est attendue pour mars. Le personnel, lui, est payé le 5.',
    subtitle: 'Les associations ont des contraintes financières uniques : subventions décalées, dépenses contraintes, rapport au CA obligatoire. Trezoone a été conçu pour ça.',
    ctaLabel: 'Tester gratuitement',
  },
  pilierLink: {
    label: 'Guide complet : gestion trésorerie association',
    href: '/gestion-tresorerie-association',
  },
  sections: [
    {
      type: 'hook',
      title: 'La réalité du trésorier d\'association',
      content: 'Être trésorier d\'association, c\'est souvent gérer des finances complexes avec peu de temps, peu de formation et des outils inadaptés.',
      items: [
        'Vous gérez plusieurs financeurs avec des calendriers différents (CAF, Région, Mairie, Europe…)',
        'Vos dépenses sont mensuelles et contraintes. Vos recettes arrivent quand elles veulent.',
        'Vous devez présenter des comptes clairs à un CA composé de bénévoles non-financiers',
        'En cas de problème, vous êtes personnellement responsable légalement',
      ],
    },
    {
      type: 'explain',
      title: 'Pourquoi Excel ne suffit plus pour une association',
      content: 'Excel est un outil généraliste. La gestion d\'une association a des spécificités que Excel ne gère pas bien :',
      items: [
        'Suivi par financeur : impossible sans macros complexes dans Excel',
        'Alertes automatiques en cas de retard de versement : inexistant dans Excel',
        'Projection du solde en prenant en compte les versements partiels : manuel et fragile',
        'Rapport PDF pour le CA en un clic : pas disponible dans Excel standard',
        'Traçabilité des opérations pour les commissaires aux comptes : difficile à maintenir',
      ],
    },
    {
      type: 'example',
      title: 'Comment une MJC utilise Trezoone au quotidien',
      content: 'La MJC "Espace Jeunes" a 4 salariés, 6 financeurs, et un budget de 280 000 €/an.',
      items: [
        'Chaque mois : import automatique du relevé bancaire → transactions catégorisées en 5 minutes',
        'Suivi des 6 subventions : dates de demande, montants attendus, versements reçus, soldes',
        'Alerte reçue en mars : "La subvention Région (22 000 €) n\'a pas été versée — solde critique dans 6 semaines"',
        'Action : relance du financeur + découvert autorisé négocié avec la banque en avance',
        'Rapport mensuel généré en 2 clics pour le CA : solde, prévision 6 mois, état des subventions',
      ],
    },
    {
      type: 'midcta',
      title: 'Une MJC avec 6 financeurs le fait en 5 minutes par mois — essayez',
      content: 'Essai gratuit 14 jours · Subventions, multi-comptes, rapports CA inclus',
    },
    {
      type: 'solution',
      title: 'Ce que Trezoone fait pour votre association',
      content: 'Trezoone est l\'outil de trésorerie conçu pour les associations loi 1901 et leurs spécificités.',
      items: [
        'Suivi des subventions : montant demandé, attendu, reçu, en retard',
        'Plan de trésorerie annuel intégrant vos cycles de versement habituels',
        'Alertes automatiques quand un versement est en retard',
        'Import CSV/OFX depuis votre banque associative (Crédit Coopératif, BNP, Crédit Agricole…)',
        'Tableau de bord simplifié pour les bénévoles non-financiers',
        'Export PDF des rapports pour le CA et les commissaires aux comptes',
        'Module de simulation : "Si la CAF verse 2 mois plus tard, combien de temps tient-on ?"',
      ],
    },
    {
      type: 'faq',
      title: 'Questions fréquentes',
      faq: [
        {
          q: 'Trezoone convient-il aux petites associations ?',
          a: 'Oui. Trezoone est conçu pour des associations de toutes tailles. Pour les petites associations (budget < 50 000 €), le pack Essentiel à 59 €/mois offre les fonctionnalités de base nécessaires.',
        },
        {
          q: 'Peut-on gérer plusieurs comptes bancaires dans Trezoone ?',
          a: 'Oui. Trezoone supporte la gestion multi-comptes (compte courant, livret, comptes dédiés par activité). Les flux sont consolidés dans un tableau de bord unique.',
        },
        {
          q: 'Trezoone peut-il aider à préparer un rapport pour le CA ?',
          a: 'Oui. Trezoone génère des rapports PDF clairs avec solde actuel, prévision, état des subventions et indicateurs clés. Conçus pour être présentés à un conseil d\'administration.',
        },
        {
          q: 'Est-ce qu\'on peut suivre plusieurs financeurs séparément ?',
          a: 'Oui. Chaque subvention peut être saisie avec son financeur, son montant, sa date attendue et son état (en attente, versée, en retard). Le tableau de suivi subventions consolide tout.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Trésorerie d\'association : tous les sujets',
      links: [
        { label: 'Gestion trésorerie association', href: '/gestion-tresorerie-association' },
        { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
        { label: 'Plan trésorerie association', href: '/plan-tresorerie-association-guide-complet' },
        { label: 'Excel trésorerie association', href: '/excel-tresorerie-association' },
        { label: 'Subvention en retard trésorerie', href: '/subvention-en-retard-tresorerie' },
        { label: 'Association loi 1901', href: '/tresorerie-association-loi-1901' },
        { label: 'Suivi subventions (fonctionnalité)', href: '/fonctionnalites/suivi-subventions' },
        { label: 'Reporting association', href: '/reporting-financier-association' },
      ],
    },
    {
      type: 'cta',
      title: 'Votre association mérite un outil dédié',
      content: 'Pas un tableur bricolé. Un outil qui comprend vos subventions, vos cycles et vos obligations. Essai gratuit 14 jours.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie association', href: '/gestion-tresorerie-association' },
    { label: 'Logiciel trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Suivi subventions', href: '/fonctionnalites/suivi-subventions' },
    { label: 'Tarifs', href: '/tarifs' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function LogicielTresorerieAssociationPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
